// The outbox — the persisted send queue (Brief 5 / point 15, the comms artery).
//
// CANON: anything the user sends is NEVER lost. A send is "optimistically"
// enqueued here BEFORE the network is touched, persisted to `localStorage`, and
// removed ONLY on confirmed server success. If the send fails (offline, dropped
// connection, server down) the entry stays — shown as pending, replayed on
// reconnect (the `online` + `visibilitychange` flush in `useOutbox`). A reload
// or crash mid-send re-hydrates the queue, so the message survives.
//
// Idempotency: every entry carries a client-minted `key`. On replay it's passed
// to `sendThreadMessage` as the `Idempotency-Key` header so a send whose request
// landed but whose RESPONSE was lost can't double-post once backend dedup ships
// (the follow-up below). Even with no backend dedup, this store never
// double-enqueues a given key, and the channel hook de-dupes the returned row on
// the server id — so a reload can't replay a message the server already stored
// and echoed.
//
//   ── BACKEND FOLLOW-UP (do NOT change the backend here) ─────────────────────
//   `POST /threads/:id/messages` should honour an `Idempotency-Key` header:
//   first write wins, a repeat returns the SAME stored row (not a second one).
//   Until then the lost-response window is covered only client-side (an entry
//   clears on success; a true network replay after a lost 2xx could still
//   double-post server-side). Tracked for the backend/orchestrator owner.
//   ───────────────────────────────────────────────────────────────────────────
//
// This module is a small framework-free store (the codebase convention, cf.
// `desktopDiagnostics.ts`): pure state + persistence + a subscribe seam, so the
// queue logic is unit-tested with no React and no live backend. `useOutbox`
// wraps it for components.

import { OUTBOX_KEY, newClientKey, readJson, removeRaw, writeJson } from "./persistence";

/** A queued send: everything needed to replay it verbatim later, plus the
 * idempotency key and bookkeeping for the pending UI. */
export interface OutboxEntry {
  /** Client idempotency key (also the React list key + the dedup key). */
  key: string;
  /** The thread this send belongs to. */
  threadId: number;
  /** The exact text the user wrote/dictated — preserved byte-for-byte. */
  body: string;
  /** Product-side-only aside (Brief 3 D6), carried through a replay. */
  aside?: boolean;
  /** When it was first enqueued (ms epoch) — replay drains oldest-first. */
  createdAt: number;
  /** Attempt count, bumped each replay (for a "still trying…" affordance). */
  attempts: number;
  /** Last failure message, for an honest "couldn't send: …" line. */
  lastError?: string;
  /** Set once a send has failed `MAX_AUTO_ATTEMPTS` times — the auto-retry gives
   * up so a permanently-rejected message (a 4xx: not-a-member, thread gone)
   * can't block the queue head forever or hammer the wire. It stays QUEUED and
   * visible (the user keeps their words + the error) for a manual retry/dismiss;
   * a manual `retry(key)` clears the flag and tries again. */
  failed?: boolean;
}

/** Auto-retry budget before an entry is parked as permanently failed. Generous,
 * since most failures are transient (offline) and clear on the first reconnect;
 * a true 4xx will exhaust it quickly and then stop nagging the wire. */
export const MAX_AUTO_ATTEMPTS = 8;

/** Narrow an unknown parsed value to a well-formed `OutboxEntry`, so a
 * corrupted or partial persisted row is dropped rather than replayed blind. */
function isEntry(v: unknown): v is OutboxEntry {
  if (!v || typeof v !== "object") return false;
  const e = v as Partial<OutboxEntry>;
  return (
    typeof e.key === "string" &&
    typeof e.threadId === "number" &&
    typeof e.body === "string" &&
    typeof e.createdAt === "number" &&
    typeof e.attempts === "number"
  );
}

/** The subscribe seam — same-tab listeners (a state change in this tab) plus
 * the browser `storage` event (a change in ANOTHER tab) both notify. */
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

/** Subscribe to queue changes; returns an unsubscribe. Cross-tab `storage`
 * events are bridged in too, so two open tabs converge on one queue. */
export function subscribeOutbox(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === OUTBOX_KEY) listener();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(listener);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

/** The whole queue, oldest-first (replay order), as persisted. Total: a missing
 * or corrupt store reads as empty, never throws. */
export function readOutbox(): OutboxEntry[] {
  const list = readJson<unknown[]>(OUTBOX_KEY, []);
  if (!Array.isArray(list)) return [];
  const entries = list.filter(isEntry);
  // Sort oldest-first, but tie-break on the PERSISTED (insertion) order so two
  // sends enqueued in the same millisecond keep a deterministic order — Array
  // sort is not reliably stable across all engines for the tied pair, and a
  // flipped order would send the user's two messages out of sequence.
  return entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) => a.e.createdAt - b.e.createdAt || a.i - b.i)
    .map(({ e }) => e);
}

/** The pending sends for ONE thread, oldest-first — what a composer renders
 * beneath the confirmed messages. */
export function readThreadOutbox(threadId: number): OutboxEntry[] {
  return readOutbox().filter((e) => e.threadId === threadId);
}

/** Persist the whole queue + notify. Empty queue removes the key (no stale
 * empty array lingering in storage). */
function persist(entries: OutboxEntry[]): void {
  if (entries.length === 0) {
    removeRaw(OUTBOX_KEY);
  } else {
    writeJson(OUTBOX_KEY, entries);
  }
  notify();
}

/**
 * Enqueue a send. Returns the created entry (its `key` is the idempotency key).
 * Trims a blank body to nothing and refuses it (an empty send is never queued).
 * The body is captured verbatim — this is the persisted copy that survives a
 * reload/crash, so the user's words are safe the instant they hit Send.
 */
export function enqueue(input: {
  threadId: number;
  body: string;
  aside?: boolean;
}): OutboxEntry | null {
  if (input.body.trim() === "") return null;
  const entry: OutboxEntry = {
    key: newClientKey(),
    threadId: input.threadId,
    body: input.body,
    aside: input.aside,
    createdAt: Date.now(),
    attempts: 0,
  };
  persist([...readOutbox(), entry]);
  return entry;
}

/** Remove an entry by key — called ONLY after the server confirms the send
 * (point 15: "clears only on confirmed success"). Idempotent. */
export function remove(key: string): void {
  const next = readOutbox().filter((e) => e.key !== key);
  persist(next);
}

/** Mark an attempt's outcome on a still-queued entry (bumps `attempts`, records
 * the error, and parks it as `failed` once the auto-retry budget is spent). A
 * no-op if the entry was already cleared (a concurrent success). */
export function markAttempt(key: string, error: string): void {
  const all = readOutbox();
  const idx = all.findIndex((e) => e.key === key);
  if (idx === -1) return;
  const attempts = all[idx].attempts + 1;
  const next = all.slice();
  next[idx] = {
    ...next[idx],
    attempts,
    lastError: error,
    failed: attempts >= MAX_AUTO_ATTEMPTS,
  };
  persist(next);
}

/** Manually re-arm a parked (`failed`) entry so the next flush attempts it again
 * — the "Retry" affordance on a permanently-failed send. */
export function retry(key: string): void {
  const all = readOutbox();
  const idx = all.findIndex((e) => e.key === key);
  if (idx === -1) return;
  const next = all.slice();
  next[idx] = { ...next[idx], failed: false, attempts: 0 };
  persist(next);
}

/** The injected sender: how an entry actually goes to the wire. Returns the
 * server-stamped row on success; rejects on failure (the entry is kept). The
 * idempotency `key` is the entry's key, so a replay is dedupable server-side. */
export type OutboxSender = (entry: OutboxEntry) => Promise<{ id: number }>;

/** What `onSent` receives so the caller can reconcile its message list with the
 * server-stamped row (dedup on `id`). */
export interface OutboxFlushResult {
  entry: OutboxEntry;
  sent: { id: number };
}

// A MODULE-LEVEL single-flight lock with request-coalescing. The queue is global
// (one `localStorage` key), so a drain must be too: two `useOutbox` instances, or
// an `online` and a `visibilitychange` firing together, must never run two
// drains at once — that is the only way the same key could be sent twice. While
// a drain is in flight, further `flushOutbox` calls JOIN it AND set `redrain`, so
// the in-flight drain takes one more pass before finishing — guaranteeing a
// message enqueued mid-drain is delivered without waiting for a fresh trigger
// (closes the enqueue-just-as-the-drain-ends race).
//
// `draining` is a SYNCHRONOUS flag set before the async work starts. It must not
// rely on `inFlight` being assigned, because an `async` IIFE begins running its
// body (synchronously, up to its first real await — which includes calling into
// `send`) BEFORE the returned promise is assigned to `inFlight`. A sender that
// re-enters `flushOutbox` in that window would otherwise see `inFlight === null`
// and start a second, recursive drain (an infinite loop). The boolean closes it.
let draining = false;
let inFlight: Promise<void> | null = null;
let redrain = false;

/**
 * Replay every queued entry through `send`, oldest-first, ONE at a time (so the
 * thread keeps the user's order and a half-drained queue is consistent). Each
 * entry is removed only on confirmed success; a failure stops the flush (the
 * remaining entries stay queued for the next reconnect) and records the error.
 * `onSent` fires per success with the server row, letting the channel hook merge
 * it (de-duped on id) without a re-read.
 *
 * Single-flight + coalescing across the whole app, so it is safe to call from
 * multiple hooks / reconnect signals (or even re-entrantly from a sender)
 * without risking a double-send, and a send enqueued while a drain is running is
 * never stranded.
 */
export function flushOutbox(
  send: OutboxSender,
  onSent?: (r: OutboxFlushResult) => void,
): Promise<void> {
  if (draining) {
    redrain = true;
    return inFlight ?? Promise.resolve();
  }
  draining = true;
  inFlight = (async () => {
    try {
      // Re-drain while new work was requested mid-flight — but stop the moment a
      // pass FAILS (the wire is down; retrying immediately would hammer it and
      // can't make progress past the stuck head). A failed pass waits for the
      // next reconnect trigger (`online` / `visibilitychange`) to try again.
      let failed = false;
      do {
        redrain = false;
        failed = await drain(send, onSent);
      } while (redrain && !failed);
    } finally {
      draining = false;
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Drain oldest-first until the queue is empty or a send fails. Re-reads each
 * pass so a message enqueued DURING the drain still goes out in this run, after
 * the existing backlog (correct order). Returns true iff a send FAILED — the
 * caller uses that to decide whether to keep re-draining or back off. */
async function drain(
  send: OutboxSender,
  onSent?: (r: OutboxFlushResult) => void,
): Promise<boolean> {
  for (;;) {
    const queue = readOutbox();
    // Send the oldest entry that hasn't been PARKED as permanently failed —
    // skipping a parked one means a poisoned 4xx can't block the messages behind
    // it (they still go out); the parked entry waits for a manual retry/dismiss.
    const entry = queue.find((e) => !e.failed);
    if (!entry) return false;
    try {
      const sent = await send(entry);
      remove(entry.key);
      onSent?.({ entry, sent });
    } catch (err) {
      markAttempt(entry.key, errorMessage(err));
      // Stop on this failure: a transient outage means the rest would fail too,
      // so back off (a reconnect / the retry interval tries again). If this was
      // the attempt that PARKED the entry, the next pass skips it and continues
      // to the messages behind it.
      if (readOutbox().some((e) => e.key === entry.key && e.failed)) {
        // It just got parked — continue draining the rest rather than backing
        // off on a permanent error.
        continue;
      }
      return true;
    }
  }
}

/** A short, safe message from any thrown value (mirrors the codebase's
 * `errorToDetail`, kept local so the store has no cross-module dep). */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "send failed";
}

/** Test-only: empty the queue + storage and reset the flush lock (so a suite
 * starts clean and no in-flight state leaks between cases). */
export function _resetOutboxForTests(): void {
  draining = false;
  inFlight = null;
  redrain = false;
  persist([]);
}
